import { zodResolver } from "@hookform/resolvers/zod";
import * as zod from "zod";

// Loose schema covering both AuthenticationResponseJSON and RegistrationResponseJSON shapes.
// We re-validate structure inside SimpleWebAuthn; this only blocks obvious garbage at the boundary.
const passkeyResponseSchema = zod
  .object({
    id: zod.string().min(1).max(1024),
    rawId: zod.string().min(1).max(1024),
    type: zod.literal("public-key"),
    response: zod.record(zod.unknown()),
    clientExtensionResults: zod.record(zod.unknown()).optional(),
    authenticatorAttachment: zod.string().optional(),
  })
  .strict()
  .or(zod.string().min(1).max(8192));

// Known _action values across both consumers of this schema:
// - login/route.tsx: "check-email" | "verify-passkey" | "register-passkey"
//   | "enroll-device-key" | "verify-device-key" | undefined (plain password submit)
// - set2fa/route.tsx, twoFaAction: always undefined (uses `formAction` instead)
const LOGIN_ACTIONS = ["check-email", "verify-passkey", "register-passkey", "enroll-device-key", "verify-device-key"] as const;

export const loginValidationSchema = zod
  .object({
    email: zod
      .string()
      .trim()
      .email("Email is not valid")
      .max(254)
      .optional()
      .or(zod.literal("").transform(() => undefined)),
    password: zod.coerce.string().max(256).optional(),
    deviceFingerPrint: zod.string().max(256).optional(),
    token: zod.coerce.string().max(16).optional(),
    formAction: zod.coerce.string().max(64).optional(),
    _action: zod.enum(LOGIN_ACTIONS).optional(),
    passkeyResponse: passkeyResponseSchema.optional(),
    loginContextToken: zod.string().max(128).optional(),
    devicePlatform: zod.coerce.string().max(64).optional(),
    devicePlatformVersion: zod.coerce.string().max(64).optional(),
    deviceBrowserName: zod.coerce.string().max(64).optional(),
    deviceBrowserVersion: zod.coerce.string().max(64).optional(),
    deviceIsMobile: zod.coerce.string().max(8).optional(),
    // Base64 SPKI-encoded ECDSA P-256 public key, sent once from enroll-device-key.
    devicePublicKey: zod.string().max(2048).optional(),
    // Base64 ECDSA signature over the server-issued device-key challenge.
    deviceKeySignature: zod.string().max(2048).optional(),
    // Sent on a plain password+2FA submit (_action undefined) when the user's passkey ceremony
    // could never succeed on this browser (no local WebAuthn credential for an account that
    // already has one elsewhere) — forces mandatory passkey registration for THIS device once
    // identity is proven fresh on this same request. See auth.server.ts's initial-password branch.
    // zod.coerce.string(), not zod.literal("true") — createFormData/parseFormData round-trips the
    // literal string "true" through JSON.parse on the server, arriving as the BOOLEAN true, not
    // the string "true". A bare zod.literal("true") rejects that boolean outright (invalid_literal),
    // silently failing the whole form's validation with no visible error (onInvalid is a no-op) —
    // exactly what broke this submit. Coerce first so both the boolean and the string are accepted,
    // matching the same pattern already used for deviceIsMobile above.
    forceRegisterNewDevice: zod.coerce.string().max(8).optional(),
  })
  // Per-action required companions. This tightens the boundary check that previously only existed
  // as ad hoc `if` guards in auth.server.ts (e.g. line ~831) — those guards stay as defense in
  // depth, this just rejects obviously-malformed requests before they reach that logic.
  .superRefine((val, ctx) => {
    if (val._action === "check-email" && !val.email) {
      ctx.addIssue({ code: zod.ZodIssueCode.custom, path: ["email"], message: "email is required for check-email" });
    }
    if ((val._action === "verify-passkey" || val._action === "register-passkey") && !val.passkeyResponse) {
      ctx.addIssue({ code: zod.ZodIssueCode.custom, path: ["passkeyResponse"], message: "passkeyResponse is required" });
    }
    if ((val._action === "verify-passkey" || val._action === "register-passkey") && !val.loginContextToken) {
      ctx.addIssue({ code: zod.ZodIssueCode.custom, path: ["loginContextToken"], message: "loginContextToken is required" });
    }
    if (val._action === "enroll-device-key" && !val.devicePublicKey) {
      ctx.addIssue({ code: zod.ZodIssueCode.custom, path: ["devicePublicKey"], message: "devicePublicKey is required" });
    }
    if (val._action === "verify-device-key" && !val.deviceKeySignature) {
      ctx.addIssue({ code: zod.ZodIssueCode.custom, path: ["deviceKeySignature"], message: "deviceKeySignature is required" });
    }
    if (val._action === "verify-device-key" && !val.devicePublicKey) {
      ctx.addIssue({ code: zod.ZodIssueCode.custom, path: ["devicePublicKey"], message: "devicePublicKey is required" });
    }
    if ((val._action === "enroll-device-key" || val._action === "verify-device-key") && !val.loginContextToken) {
      ctx.addIssue({ code: zod.ZodIssueCode.custom, path: ["loginContextToken"], message: "loginContextToken is required" });
    }
  });

export const loginResolver = zodResolver(loginValidationSchema);
export type LoginResolverType = zod.TypeOf<typeof loginValidationSchema>;
